'use client';

import React, { useMemo, useState } from 'react';
import Image from 'next/image';
import Typography from '@ui/Typography';
import { ChevronDown, ChevronUp, LockIcon, Plus } from 'lucide-react';
import Avatar from '@/components/next-ui/Avatar';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { NEKI_LOGO_S3_URL } from '@/constants/neki.constant';
import useConfirmDialogStore from '@/components/Dialogs/confirmDialog.store';
import { switchUserCommunity } from '@/actions/switch-community.action';
import { get, isEmpty } from 'lodash-es';
import { useIsCommunityAdmin } from '@/hooks/useUser';
import { cx } from 'cva';
import Link from '@ui/Link';

function getGroupAddress(address) {
  try {
    const { city, state, country } = JSON.parse(address);
    const location = [city, state, country].filter((item) => !!item);
    if (location.length > 0) {
      return location.join(', ');
    } else {
      return address;
    }
  } catch (e) {
    return address;
  }
}

function getGroups(user) {
  const primaryId = user.activeCommunity?.primaryCommunity?.id;
  const parent = user.communities.find((c) => c.id === primaryId);
  if (!isEmpty(parent?.groups)) return parent.groups;

  const active = user.communities.find((c) => c.id === user.activeCommunityId);
  if (!isEmpty(active?.groups)) return active.groups;

  return [];
}

function getOtherGroups(user) {
  const primaryId = user.activeCommunity?.primaryCommunity?.id;
  const parent = user.communities.find((c) => c.id === primaryId);
  if (!isEmpty(parent?.otherGroups)) return parent.otherGroups;

  const active = user.communities.find((c) => c.id === user.activeCommunityId);
  if (!isEmpty(active?.otherGroups)) return active.otherGroups;

  return [];
}

function GroupsList({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const { seekConfirmation } = useConfirmDialogStore();

  const groups = useMemo(() => getGroups(user), [user]);
  const otherGroups = useMemo(() => getOtherGroups(user), [user]);
  const isPrimaryCommunity = user?.activeCommunity?.primaryCommunity;

  const isCommunityAdmin = useIsCommunityAdmin();

  const toggleList = () => setIsOpen((prev) => !prev);

  const getCommunityLogoUrl = (entity) => {
    if (entity.logoUrl) {
      return getQualifiedImageUrl(entity.logoUrl);
    }
    const defaultLogoUrl = isPrimaryCommunity?.logoUrl;
    return defaultLogoUrl
      ? getQualifiedImageUrl(defaultLogoUrl)
      : NEKI_LOGO_S3_URL;
  };

  const isGroupSame = (id) => {
    return user.activeCommunityId === id;
  };

  const handleSwitchGroup = async (group) => {
    if (isGroupSame(group.id)) {
      return;
    }
    const confirmed = await seekConfirmation(
      `Switch Group`,
      `Do you want to switch to ${group.name} group?`,
      'Yes, Go ahead',
    );
    if (confirmed) {
      switchUserCommunity(group.id);
    }
  };

  if (isEmpty(groups) && isEmpty(otherGroups)) return null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4">
        <Image
          width={40}
          height={40}
          src="/assets/icons/my-groups.svg"
          alt="my groups"
          className="h-10 w-10"
        />
        <Typography
          variant="h3"
          role="button"
          tabIndex={0}
          onClick={toggleList}
          onKeyDown={(e) => e.key === 'Enter' && toggleList()}
          className="flex items-center gap-2 text-left hover:cursor-pointer hover:text-text-hyperlink focus:outline-none">
          Groups
          {isOpen ? (
            <ChevronUp className="h-5 w-5 transition-transform duration-300" />
          ) : (
            <ChevronDown className="h-5 w-5 transition-transform duration-300" />
          )}
        </Typography>
      </div>

      <div
        className={cx(
          'ml-8 overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'mt-2 max-h-[500px]' : 'max-h-0',
        )}>
        {/* <AiOutlinePlusCircle className="h-6 w-6" /> */}
        {/* {!isPrimaryCommunity && isCommunityAdmin && (
          <Typography
            title="Create Group"
            component="a"
            href={`${process.env.NEXT_PUBLIC_ADMIN_APP_URL}/apps/groups`}
            variant="subtitle1"
            className="mb-2 flex cursor-pointer items-center gap-4 font-medium hover:text-text-hyperlink">
            Create Group
          </Typography>
        )} */}

        {!isEmpty(groups) && (
          <Typography
            title="My Groups"
            variant="subtitle1"
            className="my-3 flex items-center gap-2 font-medium">
            My Groups
          </Typography>
        )}

        {!isEmpty(groups) && (
          <ul className="mt-2 flex max-w-[250px] flex-col gap-4">
            {groups.map((g) => (
              <li
                key={g.id}
                className={cx(
                  'group flex items-center gap-4 truncate',
                  !isGroupSame(g.id) && 'cursor-pointer',
                )}
                onClick={() => handleSwitchGroup(g)}>
                <div className="relative">
                  <Avatar
                    src={getCommunityLogoUrl(g)}
                    alt={g.name}
                    profile="company"
                    size="xs"
                  />
                  {!g?.publicProfile?.enabled && (
                    <span
                      className="absolute bottom-0 right-0 rounded-full"
                      title="Private">
                      <LockIcon className="h-3.5 w-3.5 fill-current text-text-secondary" />
                    </span>
                  )}
                </div>
                <Typography
                  variant="subtitle1"
                  component="span"
                  color="textSecondary"
                  className={cx(
                    'truncate font-medium',
                    !isGroupSame(g.id) && 'group-hover:text-text-hyperlink',
                  )}>
                  {g.name}
                </Typography>
              </li>
            ))}
          </ul>
        )}

        {!isEmpty(otherGroups) && (
          <Typography
            title="Explore Groups"
            variant="subtitle1"
            className="my-3 flex items-center gap-2 font-medium">
            Explore Groups
          </Typography>
        )}
        {!isEmpty(otherGroups) && (
          <div className="mt-2 flex max-w-[250px] flex-col gap-4">
            {otherGroups.map((g) => {
              const isPublic = g?.publicProfile?.enabled;
              const content = (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar
                      src={getCommunityLogoUrl(g)}
                      alt={g.name}
                      profile="company"
                      size="xs"
                    />
                    {!isPublic && (
                      <span
                        className="absolute bottom-0 right-0 rounded-full"
                        title="Private">
                        <LockIcon className="h-3.5 w-3.5 fill-current text-text-secondary" />
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col items-start justify-between">
                    <Typography
                      variant="subtitle1"
                      className={
                        isPublic ? 'group-hover:text-text-hyperlink' : ''
                      }>
                      {g.name}
                    </Typography>
                    {/* <Typography variant="body1" color="textSecondary">
                        {getGroupAddress(g.address)}
                      </Typography> */}
                  </div>
                </div>
              );

              return isPublic ? (
                <Link key={g.id} href={`/${get(g, 'publicProfile.slug')}`}>
                  {content}
                </Link>
              ) : (
                <div key={g.id}>{content}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupsList;
